import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { Section, SectionTitle, PageHero } from '@/components/shared/Section';
import { Quote, ExternalLink, ArrowRight, Users, Heart, Shield, Lightbulb, Flame, RefreshCw, Anchor } from 'lucide-react';

const team = [
  { name: 'Mario Pino Corona', role: 'CEO & Fundador', photo: '/allcom_mario_pino_color.jpg', linkedin: 'https://www.linkedin.com/in/mario-pino-corona-86733550/' },
  { name: 'José de Amorin Parejo', role: 'CIO — Director de Tecnología & Infraestructura', photo: '/allcom_jose_amorin.jpg' },
  { name: 'Rodrigo Mancilla Moreno', role: 'Líder de Desarrollo', photo: '/allcom_rodrigo_mancilla.jpg' },
  { 
    name: 'Kian', 
    role: 'CAIO — Chief AI Officer', 
    photo: '/kian_caio.webp',
    description: 'Dirige los sistemas de IA de ALLCOM. Acceso total a software, desarrollo y apoyo en decisiones estratégicas.',
    trademark: true,
    isAI: true
  },
];

const values = [
  { icon: Anchor, title: 'Lealtad', desc: 'Con nuestros clientes, partners y colaboradores. Los compromisos se cumplen.' },
  { icon: Heart, title: 'Compromiso', desc: 'Hacemos nuestro cada proyecto. El resultado del cliente es nuestro resultado.' },
  { icon: Lightbulb, title: 'Creatividad', desc: 'Resolvemos problemas complejos con soluciones simples e innovadoras.' },
  { icon: Flame, title: 'Pasión por resolver', desc: 'Nos mueve encontrar la solución, no importa cuántas veces haya que intentar.' },
  { icon: RefreshCw, title: 'Adaptabilidad', desc: 'Cambiamos, aprendemos y evolucionamos. La rigidez no tiene lugar aquí.' },
  { icon: Shield, title: 'Resiliencia', desc: '+20 años de caídas y aprendizajes nos hicieron más fuertes y confiables.' },
];

export default function CrewPage() {
  return (
    <div>
      <PageHero badge="NUESTRO EQUIPO" badgeIcon={Users} h1="El crew detrás de ALLCOM" subtitle="Creatividad, empatía, liderazgo y confianza. Un equipo multidisciplinario comprometido con tu operación."
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Conversemos <ArrowRight className="w-4 h-4" /></Link>}
      />

      {/* Team Photo - Hands */}
      <Section>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-12">
          <img src="/allcom_equipo_manos_opt.jpg" alt="Equipo ALLCOM - Compromiso y unión" className="w-full h-64 sm:h-80 lg:h-96 object-cover" style={{ filter: 'grayscale(100%)' }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F]/80 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <p className="text-white text-lg font-semibold" style={{fontFamily:'Syne,sans-serif'}}>Equipo ALLCOM</p>
            <p className="text-slate-300 text-sm">Ingenieros, técnicos y líderes que forman un equipo dinámico, comprometido y llenos de capacidad de adaptación.</p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <SectionTitle badge="NUESTROS VALORES" title="Lo que nos define" center />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {values.map((v, i) => (
              <div key={i} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
                <v.icon className="w-6 h-6 text-[#0A2540] mx-auto mb-3" strokeWidth={1.6} />
                <h3 className="text-sm font-semibold mb-1" style={{fontFamily:'Syne,sans-serif'}}>{v.title}</h3>
                <p className="text-xs text-[#64748B] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team Members */}
        <SectionTitle badge="LÍDERES" title="Conoce al equipo" center />
        <motion.div variants={stagger} className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {team.map((m, i) => (
            <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm card-hover text-center">
              {m.isAI ? (
                <img src={m.photo} alt={m.name} className="w-32 h-32 mx-auto mb-4 rounded-full object-cover shadow-2xl ring-4 ring-[#0EA5E9]/20" />
              ) : (
                <img src={m.photo} alt={m.name} className="w-32 h-32 mx-auto mb-4 rounded-full object-cover object-top shadow-lg" />
              )}
              <h3 className="text-base font-semibold" style={{fontFamily:'Syne,sans-serif'}}>
                {m.name}{m.trademark && <span className="text-sm align-super">™</span>}
              </h3>
              <p className="text-xs font-medium text-[#0EA5E9] mb-2">{m.role}</p>
              {m.description && (
                <p className="text-xs text-slate-600 leading-relaxed">{m.description}</p>
              )}
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Reflection */}
      <Section dark>
        <div className="max-w-3xl mx-auto text-center">
          <Quote className="w-8 h-8 text-[#0EA5E9] mx-auto mb-5" />
          <blockquote className="text-lg sm:text-xl text-white font-medium leading-relaxed mb-6" style={{fontFamily:'Syne,sans-serif'}}>
            "La tecnología avanza cuando las personas que la construyen entienden que liderar es servir, que evolucionar es inevitable y que lo mejor siempre está por venir."
          </blockquote>
          <div className="accent-line w-16 mx-auto mb-8" />
          <a href="https://www.marioandres.cl" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-6 py-3 text-base font-semibold text-[#0EA5E9] hover:text-white border border-[#0EA5E9]/30 hover:bg-[#0EA5E9]/10 rounded-xl transition-colors">
            <ExternalLink className="w-5 h-5" /> www.marioandres.cl
          </a>
        </div>
      </Section>
    </div>
  );
}
